import * as React from 'react'
import { Link as RouterLink } from 'react-router-dom'
import {
  Container,
  Box,
  Grid,
  Typography,
  Link,
  Divider,
  IconButton
} from '@mui/material'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import logo from '../assets/logo/123back-white.png'

// i18n
import { useI18n } from '../i18n/I18nProvider'
import footerI18n from '../i18n/components/footer.i18n'

/** Les liens sont i18n-isés via leur id */
const navItems = [
  { id: 'services',    to: '/services' },
  { id: 'pricing',     to: '/tarifs' },
  { id: 'secureSend',  to: '/envoi-securise' },
  { id: 'criteria',    to: '/criteres-rachat' },
  { id: 'myShipments', to: '/mes-envois' },
  { id: 'cookies',     to: '/cookies' }, // ✅ ajouté
]

export default function Footer() {
  const { t, register } = useI18n()

  // Enregistre les traductions du footer (comme pour la navbar)
  React.useEffect(() => {
    register('footer', footerI18n)
  }, [register])

  return (
    <Box component="footer" sx={{ bgcolor: '#0466c8', color: '#fff', mt: { xs: 6, md: 6 } }}>
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Grid container spacing={{ xs: 3, md: 4 }} alignItems="flex-start">
          {/* Col 1 : Logo + baseline */}
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
              <Box
                component={RouterLink}
                to="/services"
                sx={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }}
              >
                <Box component="img" src={logo} alt="123Back" sx={{ height: 25, width: 'auto' }} />
              </Box>
            </Box>
            <Typography sx={{ opacity: 0.95, lineHeight: 1.6 }}>
              {t('footer.baseline')}
            </Typography>
          </Grid>

          {/* Col 2-3 : Navigation | Contact avec séparateur contrôlé */}
          <Grid item xs={12} md={8}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { xs: 'flex-start', md: 'stretch' },
                gap: { xs: 2.5, md: 3 },
              }}
            >
              {/* Navigation */}
              <Box sx={{ minWidth: { md: 260 } }}>
                <Typography sx={{ fontWeight: 800, mb: 1.25 }}>
                  {t('footer.nav.title')}
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: 0.75,
                  }}
                >
                  {navItems.map((l) => (
                    <Link
                      key={l.id}
                      component={RouterLink}
                      to={l.to}
                      underline="none"
                      sx={{
                        color: '#fff',
                        opacity: 0.95,
                        '&:hover': { opacity: 1, textDecoration: 'underline' },
                      }}
                    >
                      {t(`footer.links.${l.id}`)}
                    </Link>
                  ))}
                </Box>
              </Box>

              {/* Séparateur – vertical en md+, horizontal en mobile */}
              <Divider
                orientation="vertical"
                flexItem
                sx={{
                  borderColor: 'rgba(255,255,255,0.25)',
                  display: { xs: 'none', md: 'block' },
                }}
              />
              <Divider
                sx={{
                  borderColor: 'rgba(255,255,255,0.25)',
                  width: '100%',
                  my: 0.5,
                  display: { xs: 'block', md: 'none' },
                }}
              />

              {/* Contact */}
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 800, mb: 1.25 }}>
                  {t('footer.contact.title')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton
                    size="small"
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.15)',
                      color: '#fff',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
                    }}
                    aria-label={t('footer.contact.email_aria')}
                    component="a"
                    href="mailto:contact@123back.eu"
                  >
                    <EmailOutlinedIcon fontSize="small" />
                  </IconButton>

                  <Link
                    href="mailto:contact@123back.eu"
                    underline="none"
                    sx={{ color: '#fff', fontWeight: 700, '&:hover': { textDecoration: 'underline' } }}
                  >
                    contact@123back.eu
                  </Link>
                </Box>

                <Typography sx={{ mt: 1.5, opacity: 0.95, lineHeight: 1.6 }}>
                  {t('footer.contact.hours')}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: { xs: 3, md: 4 }, borderColor: 'rgba(255,255,255,0.25)' }} />

        {/* Bas de page */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            justifyContent: { xs: 'flex-start', sm: 'space-between' },
            gap: 1.5,
          }}
        >
          <Typography sx={{ opacity: 0.9 }}>
            © {new Date().getFullYear()} 123Back — {t('footer.legal.rights')}
          </Typography>

          {/* CGV & Mentions légales sur la même ligne */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: { xs: 'flex-start', sm: 'flex-end' },
              gap: 1,
              fontSize: 14,
            }}
          >
            <Link
              component={RouterLink}
              to="/conditions-generales-rachat"
              underline="none"
              sx={{ color: '#fff', opacity: 0.9, '&:hover': { opacity: 1, textDecoration: 'underline' } }}
            >
              {t('footer.legal.terms')}
            </Link>
            <Typography component="span" sx={{ opacity: 0.6 }}>
              |
            </Typography>
            <Link
              component={RouterLink}
              to="/mentions-legales"
              underline="none"
              sx={{ color: '#fff', opacity: 0.9, '&:hover': { opacity: 1, textDecoration: 'underline' } }}
            >
              {t('footer.legal.mentions')}
            </Link>
          </Box>
        </Box>
      </Container>

      {/* Bande finale */}
      <Box sx={{ bgcolor: '#004c98ff', py: 1.25 }}>
        <Container maxWidth="lg">
          <Typography sx={{ color: 'rgba(255,255,255,0.9)', fontSize: 12.5 }}>
            {t('footer.bottom.notice')}
          </Typography>
        </Container>
      </Box>
    </Box>
  )
}
